import Diag = require("VSS/Diag");
import Utils_Array = require("VSS/Utils/Array");
import VSS = require("VSS/VSS");
import { HtmlNormalizer } from "VSS/Utils/Html";
import * as VSS_Service from "VSS/Service";

import WITConstants = require("Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants");
import TCMConstants = require("Presentation/Scripts/TFS/Generated/TFS.TestManagement.Constants");
import TestsOM = require("TestManagement/Scripts/TFS.TestManagement");
import TMUtils = require("TestManagement/Scripts/TFS.TestManagement.Utils");
import CSVRowViewModel = require("TestManagement/Scripts/TFS.TestManagement.CSVRowViewModel");
import { TestManagementRegistrySettingsService } from "TestManagement/Scripts/TestManagementRegistrySettingsSerivce";
/**
 * TestCaseExportHelper: This is the helper class, which provides row level data for testcases CSV export.
 */

export class TestCaseExportHelper {
    private _registrySettingsService: TestManagementRegistrySettingsService;

    private c_spaceChar = ' ';
    private c_testCaseDataToPage: string[] = [
        WITConstants.CoreFieldRefNames.Id,
        WITConstants.CoreFieldRefNames.Rev,
        WITConstants.CoreFieldRefNames.Title,
        WITConstants.CoreFieldRefNames.AreaPath,
        TCMConstants.WorkItemFieldNames.Actions
    ];

    private static readonly BATCH_SIZE_KEY="/Service/TestManagement/Settings/TestCasesExportImportBatchSize";
    private _workItemBatchSize = 200;

    public initialize(testCaseIds?: number[], additionalWitFields?: string[], okCallBack?: (testCasesData: any) => void) {

        this._setBatchSize();
        if (additionalWitFields) {
            this._mergeFixedAndAdditionalFieldsList(additionalWitFields);
        }

        let allTestCases: TestsOM.TestCase[] = [];
        const end = testCaseIds.length < this._workItemBatchSize ? testCaseIds.length : this._workItemBatchSize;
        //Fetch testcase data using testCaseIds
        this._beginFetchTestCases(allTestCases, testCaseIds, 0, end, (testCases: TestsOM.TestCase[]) => {
            //Creates row level records for CSV export from the testCases
            let records: CSVRowViewModel.CSVRowViewModel[] = this._populateRowLevelRecordsForCSV(testCases);
            if (okCallBack) {
                okCallBack(records);
            }
        });
    }

    private _setBatchSize() {
        this._registrySettingsService = VSS_Service.getService(TestManagementRegistrySettingsService);
        this._registrySettingsService.getValue(TestCaseExportHelper.BATCH_SIZE_KEY,
            this._workItemBatchSize.toString()).then((batchSizeStr: string) => {
                this._workItemBatchSize = Number(batchSizeStr);
        }).catch((error) => {
            Diag.logError("[_setBatchSize] " + error);
        });
    }

    //Fetch testcase data  in batches using testCaseIds
    private _beginFetchTestCases(allTestCases: TestsOM.TestCase[], testCaseIds: number[], start: number, end: number, callback: IResultCallback) {
        this._beginFetchTestCasesInBatch(testCaseIds.slice(start, end), (testCases) => {
            allTestCases = allTestCases.concat(testCases);
            if (end === testCaseIds.length) {
                // all test cases fetched , send callback 
                callback(allTestCases);
            }
            else {
                start = end;
                end = start + this._workItemBatchSize < testCaseIds.length ? start + this._workItemBatchSize : testCaseIds.length;
                this._beginFetchTestCases(allTestCases, testCaseIds, start, end, callback);
            }
        })
    }

    private _mergeFixedAndAdditionalFieldsList(additionalWitFields) {
        let length = this.c_testCaseDataToPage.length,
            i: number;

        for (i = 0, length = additionalWitFields.length; i < length; i++) {
            this.c_testCaseDataToPage.push(additionalWitFields[i]);
        }
        this.c_testCaseDataToPage = Utils_Array.unique(this.c_testCaseDataToPage);
    }

    private _beginFetchTestCasesInBatch(testCaseIds: number[], callback?: IResultCallback) {
        TMUtils.TestCaseUtils.beginGetTestCases(testCaseIds, this.c_testCaseDataToPage, (testCases: TestsOM.TestCase[]) => {
            let sharedStepIds: number[] = this._getSharedStepsIds(testCases);

            if (sharedStepIds && sharedStepIds.length > 0) {
                this._beginFetchSharedStepsInTestCase(sharedStepIds, testCases, () => {
                    if (callback) {
                        callback(testCases);
                    }
                });
            }
            else {
                if (callback) {
                    callback(testCases);
                }
            }
        });
    }

    private _getSharedStepsIds(testCases: TestsOM.TestCase[]): number[] {
        let length = testCases.length,
            sharedStepIds: number[] = [],
            testActions: TestsOM.TestAction[],
            numSteps = 0;

        for (let i= 0; i < length; i++) {
            testActions = testCases[i].getTestSteps();
            numSteps = testActions.length;
            for (let j = 0; j < numSteps; j++) {
                if (testActions[j] instanceof TestsOM.SharedSteps) {
                    sharedStepIds.push((<TestsOM.SharedSteps>testActions[j]).ref);
                }
            }
        }

        return Utils_Array.unique(sharedStepIds);
    }

    private _beginFetchSharedStepsInTestCase(sharedStepIds: number[], testCases: TestsOM.TestCase[], callback?: IResultCallback) {
        let i = 0,
            l = testCases.length,
            testCase: TestsOM.TestCase;

        TMUtils.TestCaseUtils.beginGetSharedSteps(
            sharedStepIds,
            (sharedStepWorkItems: TestsOM.SharedStepWorkItem[]) => {
                let sharedStepIdToWorkItemMap = this._populateSharedStepsIdToWorkItemMap(sharedStepWorkItems);
                for (i = 0; i < l; i++) {
                    testCase = testCases[i];
                    testCase.setSharedStepWorkItemInTestCase(sharedStepIdToWorkItemMap);
                }
                if (callback) {
                    callback();
                }
            });
    }

    //This method creates row level records from CSV
    private _populateRowLevelRecordsForCSV(testCases: TestsOM.TestCase[]): CSVRowViewModel.CSVRowViewModel[] {
        let i = 0,
            j = 0,
            numSteps = 0,
            l = testCases.length,
            title: string,
            action: string,
            expectedResult: string,
            id: number,
            testActions: TestsOM.TestAction[],
            testStep: TestsOM.TestStep,
            records: CSVRowViewModel.CSVRowViewModel[] = []

        for (i = 0; i < l; i++) {
            title = testCases[i].getTitle();
            testActions = testCases[i].getTestSteps();
            numSteps = testActions.length;
            id = testCases[i].getId();

            records.push(new CSVRowViewModel.CSVRowViewModel(title, 0, "", "", testCases[i], true, false, 0, testCases[i].getProperties()));
            for (j = 0; j < numSteps; j++) {
                //Add steps in the records
                if (testActions[j] instanceof TestsOM.TestStep) {
                    testStep = <TestsOM.TestStep>testActions[j];
                    action = this._getInnerTextFromHtmlElement(testStep.action);
                    expectedResult = this._getInnerTextFromHtmlElement(testStep.expectedResult);
                    records.push(
                        new CSVRowViewModel.CSVRowViewModel(
                            "",
                            j + 1,
                            action.startsWith('@') ? this.c_spaceChar + action : action, //Since parameters name starts with @, so adding extra space to avoid CSV parsing issue, when column value starts with @
                            expectedResult.startsWith('@') ? this.c_spaceChar + expectedResult : expectedResult,
                            null,
                            false
                        )
                    );
                }
                else {
                    //Expand shared steps in records
                    this._expandStepsOfSharedSteps(<TestsOM.SharedSteps>testActions[j], j + 1, records);
                }

                id = undefined;
            }
        }

        return records;
    }

    private _expandStepsOfSharedSteps(sharedStep: TestsOM.SharedSteps, sharedStepIndex: number, records: CSVRowViewModel.CSVRowViewModel[]) {
        let i = 0,
            j = 0,
            numSteps = 0,
            action: string,
            expectedResult: string,
            testActions: TestsOM.TestAction[],
            testStep: TestsOM.TestStep,
            rowModel: CSVRowViewModel.CSVRowViewModel,
            subStepindex: number,
            testStepIndex: number;

        rowModel = new CSVRowViewModel.CSVRowViewModel("", sharedStepIndex, sharedStep.getTitle(), "", null, false, true, sharedStep.ref);
        records.push(rowModel);

        testActions = sharedStep.getTestSteps();
        numSteps = testActions.length;

        for (j = 0; j < numSteps; j++) {
            if (testActions[j] instanceof TestsOM.TestStep) {
                testStep = <TestsOM.TestStep>testActions[j];
                action = this._getInnerTextFromHtmlElement(testStep.action);
                expectedResult = this._getInnerTextFromHtmlElement(testStep.expectedResult);
                testStepIndex = j + 1;
                subStepindex = sharedStepIndex + (testStepIndex / Math.pow(10, testStepIndex.toString().length));
                records.push(
                    new CSVRowViewModel.CSVRowViewModel(
                        "",
                        subStepindex,
                        action.startsWith('@') ? this.c_spaceChar + action : action, //Since parameters name starts with @, so adding extra space to avoid CSV parsing issue, when column value starts with @
                        expectedResult.startsWith('@') ? this.c_spaceChar + expectedResult : expectedResult,
                        null,
                        false
                    )
                );
            }
        }

    }

    private _populateSharedStepsIdToWorkItemMap(workItems: TestsOM.SharedStepWorkItem[]): any {
        let i: number,
            len: number,
            sharedStepIdToWorkItemMap: { [id: number]: TestsOM.SharedStepWorkItem } = {};

        for (i = 0, len = workItems.length; i < len; i++) {
            sharedStepIdToWorkItemMap[workItems[i].getId()] = workItems[i];
        }

        return sharedStepIdToWorkItemMap;
    }

    private _getInnerTextFromHtmlElement(htmlString: string): string {
        let innerText: string;

        //Replacing <BR/> tag with new line to get new line in plain text
        htmlString = HtmlNormalizer.normalize(htmlString).replace(/<br\s*[\/]?>/gi, '\n');

        try {
            innerText = $(htmlString)[0].innerText;
        } catch (e) {
            innerText = $(TestsOM.HtmlUtils.wrapInDiv(htmlString))[0].innerText;
        }

        return innerText;
    }
}

VSS.tfsModuleLoaded("TFS.TestManagement.TestCaseExportHelper", exports);
